import { Box, TextField } from '@mui/material';
import { Notification, NotificationTypeEnum } from 'Components/designSystem';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from 'shared/api';
import { MobileBottomBarSubmitButton, PageContentHeading } from 'shared/components';
import { useIsMobile } from 'shared/hooks';
import { useErrorText } from 'shared/i18n';
import styled from 'styled-components';

import { MAX_JOB_PREFERENCES_FREE_TEXT_LENGTH } from '../constants';
import { formValidate } from '../helpers';
import { UpdateJobPreferencesFormData, UpdateJobPreferencesFormErrors } from '../types';
import UpdateFormActionButtons from './UpdateFormActionButtons';

interface UpdateJobPreferencesFormProps {
    onSubmit: (payload: UpdateJobPreferencesFormData) => void;
    isLoading: boolean;
    errorCode?: ErrorCode;
    onCancel?: () => void;
    defaultValue?: string;
}

export default function UpdateJobPreferencesForm({ onSubmit, errorCode, isLoading, onCancel, defaultValue }: UpdateJobPreferencesFormProps) {
    const { t } = useTranslation('jobPreferences');
    const isMobile = useIsMobile();
    const [ freeText, setFreeText ] = React.useState(defaultValue || '');
    const [ errors, setErrors ] = React.useState<UpdateJobPreferencesFormErrors | null>(null);
    const errorText = useErrorText();
    
    const handleChangeText = (value: string) => {
        setFreeText(value);
    };
    
    const handleSubmit = () => {
        const errors = formValidate({ freeText });
        setErrors(errors);
    
        if (!errors) {
            onSubmit({ freeText });
        }
    };

    return (
        <>
            <Box>
                { !isMobile && <PageContentHeading title={ t('title') }
                    actions={ <UpdateFormActionButtons onComplete={ onCancel }
                        onSubmit={ handleSubmit }
                        isLoading={ isLoading }
                        disabledSubmit={ isLoading || !freeText || freeText === defaultValue } /> }
                /> }
        
                <TextInputContainer>
                    <TextField value={ freeText } 
                        onChange={ (e) => handleChangeText(e.target.value) }
                        placeholder={ t('inputPlaceholder') }
                        required
                        error={ !!errors?.freeText }
                        slotProps={{
                            htmlInput: {
                                maxLength: MAX_JOB_PREFERENCES_FREE_TEXT_LENGTH
                            }
                        }}
                        multiline
                        fullWidth
                        minRows={ isMobile ? 20 : 5 }
                    />
                </TextInputContainer>
                { errorCode && (
                    <StyledNotification type={ NotificationTypeEnum.Error }
                        text={ errorText(errorCode) } />
                ) }
            </Box>
            <MobileBottomBarSubmitButton onClick={ handleSubmit }
                isDirty={ Boolean(freeText && freeText !== defaultValue) }
                isLoading={ isLoading } />
        </>
    );
}

const TextInputContainer = styled.div`
    padding-top: ${ ({ theme }) => theme.size.size_20 };
    padding-bottom: ${ ({ theme }) => theme.size.size_30 };
`;

const StyledNotification = styled(Notification)`
    border-radius: ${ ({ theme }) => theme.size.size_10 };
    margin-bottom: ${ ({ theme }) => theme.size.size_30 };
`;
